import { useCheckoutStore } from '@/stores/checkoutStore';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import useEventDataStore from '@/stores/eventDataStore';
import isEmpty from 'lodash/isEmpty';
import { computed, reactive, ref, watch } from 'vue';

export function useEventGuestRegistration(
  allTicketSelectionData,
  loadingTicketData,
  addonSummary,
  postOrderDetails,
  route,
  ticketMap,
) {
  const checkoutStore = useCheckoutStore();
  const snackbar = useSnackbarStore();
  const eventDataStore = useEventDataStore();

  const localSelectionsMap = reactive({});
  const orderedTicketInstances = ref([]);
  const registrations = ref({});
  const stashedData = reactive({});
  const occupiedAddons = ref({});
  const primaryRegistrantSelectionKey = ref(null);
  const pendingQtyChange = ref(null);
  const openDeleteStashedFormConfirm = ref(false);

  const tickets = computed(() => orderedTicketInstances.value);

  const selectedTicketTypes = computed(() => {
    return Object.values(localSelectionsMap).filter((item) => item.qty > 0);
  });

  const hasMultipleTicketTypes = computed(() => selectedTicketTypes.value.length > 1);

  const filledFormsCountPerType = computed(() => {
    return tickets.value.reduce((acc, ticket) => {
      const key = `${ticket.ticket_id}`;
      if (!acc[key]) acc[key] = 0;
      const stash = stashedData[ticket._instanceId];
      if (stash && Object.keys(stash).length > 0) acc[key]++;
      return acc;
    }, {});
  });

  const primaryRegistrantDropdownItems = computed(() => {
    return selectedTicketTypes.value.map((item) => {
      const key = `${item.ticket.ticket_id}`;
      const totalQty = item.qty;
      const filledQty = filledFormsCountPerType.value[key] || 0;
      const availableQty = totalQty - filledQty;
      return {
        title: `${item.ticket?.name} (${availableQty} available)`,
        value: key,
        disabled: availableQty === 0,
      };
    });
  });

  const primaryRegistrantTicket = computed(() => {
    if (!tickets.value || tickets.value.length === 0) return null;
    if (!hasMultipleTicketTypes.value || !primaryRegistrantSelectionKey.value) {
      return tickets.value[0];
    }
    return tickets.value.find((t) => `${t.ticket_id}` === primaryRegistrantSelectionKey.value);
  });

  const additionalRegistrantTickets = computed(() => {
    if (isEmpty(tickets.value) || !primaryRegistrantTicket.value) return [];
    return tickets.value.filter((t) => t._instanceId !== primaryRegistrantTicket.value._instanceId);
  });

  const displayAddOnSummary = computed(() =>
    Object.values(registrations.value).reduce((acc, curr) => {
      if (curr) {
        curr?.add_ons?.forEach((a) => {
          acc[a.name] = {
            total:
              (acc[a.name]?.total || 0) +
              (a?.variant ? Number(a.variant.price ?? a?.price ?? 0) : (a?.price ?? 0)),
            count: (acc[a.name]?.count || 0) + 1,
          };
        });
      }
      return acc;
    }, {}),
  );

  function getActiveTicketPricing(ticket) {
    const regularPrice = Number(
      ticket?.regular_price ?? ticket?.config?.regular_price ?? ticket?.config?.price ?? 0,
    );

    if (ticket?.is_early_bird && ticket?.is_early_bird_active) {
      return {
        price: Number(ticket?.early_bird_amount ?? ticket?.config?.price ?? regularPrice),
        originalPrice: regularPrice,
        type: 'early_bird',
      };
    }

    if (ticket?.is_late_registration && ticket?.is_late_registration_active) {
      return {
        price: Number(ticket?.late_registration_price ?? ticket?.config?.price ?? regularPrice),
        originalPrice: regularPrice,
        type: 'late_registration',
      };
    }

    return {
      price: Number(ticket?.config?.price ?? regularPrice),
      originalPrice: regularPrice,
      type: 'regular',
    };
  }

  const displayTicketSummary = computed(() => {
    const allSelections = Object.values(localSelectionsMap)
      .filter((item) => item.qty > 0)
      .map((item) => ({
        ticket: item.ticket,
        qty: item.qty,
      }));

    return allSelections.map((selection) => {
      let displayName = `${selection.ticket.name} (x${selection.qty})`;
      const activePricing = getActiveTicketPricing(selection.ticket);
      let price = activePricing.price * selection.qty;
      let originalPrice = activePricing.originalPrice * selection.qty;

      if (selection?.ticket?.price_type === 'donation') {
        price = selection?.ticket?.price?.config.suggested_donation ?? 0;
      }
      if (selection?.ticket?.price_type === 'gradual_discounts') {
        price = 0;
        const pricesToUse = selection?.ticket?.config.price.discount_pattern?.split(',');
        for (let i = 0; i < selection.qty; i++) {
          if (i < pricesToUse.length) {
            price += Number(pricesToUse[i]);
          } else {
            price += Number(pricesToUse[pricesToUse.length - 1]);
          }
        }
      }
      return {
        displayName,
        price,
        originalPrice,
        qty: selection.qty,
        id: selection.ticket.ticket_id,
        isEarlyBird: activePricing.type === 'early_bird',
        isLateRegistration: activePricing.type === 'late_registration',
        isDonation: selection?.ticket?.price_type === 'donation',
      };
    });
  });

  const completedRegistrationsCount = computed(() => {
    return Object.values(stashedData).filter((data) => !!data && Object.keys(data).length > 0)
      .length;
  });

  const registrationProgress = computed(() => {
    if (!tickets.value || tickets.value.length === 0) return 0;
    return (completedRegistrationsCount.value / tickets.value.length) * 100;
  });

  const disableContinue = computed(() => {
    if (!tickets.value || tickets.value.length === 0) {
      return true;
    }
    const primaryTicket = primaryRegistrantTicket.value;
    if (!primaryTicket) return true;

    let allFormsFilled = true;
    for (const ticket of tickets.value) {
      const stash = stashedData[ticket._instanceId];
      if (!stash || Object.keys(stash).length === 0) {
        allFormsFilled = false;
        break;
      }
    }
    return !allFormsFilled;
  });

  function clearLocalState() {
    Object.keys(registrations.value).forEach((key) => delete registrations.value[key]);
    Object.keys(stashedData).forEach((key) => delete stashedData[key]);
    orderedTicketInstances.value = [];
    Object.keys(localSelectionsMap).forEach((key) => {
      if (localSelectionsMap[key]) {
        localSelectionsMap[key].qty = 0;
      }
    });
    primaryRegistrantSelectionKey.value = null;
    pendingQtyChange.value = null;
    occupiedAddons.value = {};
  }

  function findInstanceIdToRemove(ticketTypeIdKey) {
    const instances = tickets.value.filter((t) => `${t.ticket_id}` === ticketTypeIdKey);
    if (!instances.length) return null;

    const primaryId = primaryRegistrantTicket.value?._instanceId;
    const additionalInstances = instances.filter((t) => t._instanceId !== primaryId);

    const emptyAdditional = [...additionalInstances].reverse().find((t) => {
      const stash = stashedData[t._instanceId];
      return !stash || Object.keys(stash).length === 0;
    });
    if (emptyAdditional) {
      return { id: emptyAdditional._instanceId, needsConfirm: false };
    }

    const filledAdditional = [...additionalInstances].reverse().find((t) => {
      const stash = stashedData[t._instanceId];
      return stash && Object.keys(stash).length > 0;
    });
    if (filledAdditional) {
      return { id: filledAdditional._instanceId, needsConfirm: true };
    }

    const primaryInstance = instances.find((t) => t._instanceId === primaryId);
    if (primaryInstance) {
      const stash = stashedData[primaryInstance._instanceId];
      const isFilled = stash && Object.keys(stash).length > 0;
      return { id: primaryInstance._instanceId, needsConfirm: isFilled };
    }
    return null;
  }

  function updatePrimaryKeyIfNeeded() {
    if (!primaryRegistrantSelectionKey.value) return;
    const currentKeyIsValid = localSelectionsMap[primaryRegistrantSelectionKey.value]?.qty > 0;

    if (!currentKeyIsValid) {
      if (tickets.value.length > 0) {
        const newFirstTicket = tickets.value[0];
        primaryRegistrantSelectionKey.value = `${newFirstTicket.ticket_id}`;
      } else {
        primaryRegistrantSelectionKey.value = null;
      }
    }
  }

  function handleQtyChange(ticket, newQty, revert) {
    const key = `${ticket.ticket_id}`;
    const numericQty = !newQty || Number.isNaN(Number(newQty)) ? 0 : Number(newQty);

    if (!localSelectionsMap[key]) {
      localSelectionsMap[key] = { ticket, qty: 0 };
    }
    const currentQty = localSelectionsMap[key]?.qty || 0;

    if (numericQty > currentQty) {
      localSelectionsMap[key].qty = numericQty;
      const numToAdd = numericQty - currentQty;

      let maxExistingIndex = -1;
      orderedTicketInstances.value.forEach((instance) => {
        if (`${instance.ticket_id}` === key) {
          const parts = instance._instanceId.split('_');
          const index = parseInt(parts[parts.length - 1], 10);
          if (!isNaN(index) && index > maxExistingIndex) {
            maxExistingIndex = index;
          }
        }
      });

      for (let i = 0; i < numToAdd; i++) {
        const instanceIndex = maxExistingIndex + 1 + i;
        const instanceId = `${ticket.ticket_id}_${instanceIndex}`;
        orderedTicketInstances.value.push({ ...ticket, _instanceId: instanceId });
      }

      if (!primaryRegistrantSelectionKey.value && orderedTicketInstances.value.length > 0) {
        const firstTicket = orderedTicketInstances.value[0];
        primaryRegistrantSelectionKey.value = `${firstTicket.ticket_id}`;
      }
      return;
    }

    if (numericQty < currentQty) {
      const toRemove = findInstanceIdToRemove(key);

      if (!toRemove) {
        console.warn('Could not find instance to remove for key:', key);
        if (typeof revert === 'function') revert(currentQty);
        return;
      }

      if (toRemove.needsConfirm) {
        pendingQtyChange.value = { key, qty: numericQty, idsToRemove: [toRemove.id] };
        openDeleteStashedFormConfirm.value = true;
        if (typeof revert === 'function') revert(currentQty);
      } else {
        delete registrations.value[toRemove.id];
        delete stashedData[toRemove.id];
        orderedTicketInstances.value = orderedTicketInstances.value.filter(
          (t) => t._instanceId !== toRemove.id,
        );
        localSelectionsMap[key].qty = numericQty;
        pendingQtyChange.value = null;
        updatePrimaryKeyIfNeeded();
      }
    }
  }

  function handleDeleteStashedForm() {
    openDeleteStashedFormConfirm.value = false;
    if (pendingQtyChange.value) {
      const { key, qty, idsToRemove } = pendingQtyChange.value;

      if (idsToRemove && idsToRemove.length > 0) {
        for (const id of idsToRemove) {
          const reg = registrations.value[id];
          if (reg && reg.add_ons && reg.add_ons.length > 0) {
            for (const addon of reg.add_ons) {
              if (occupiedAddons.value[addon.id] && occupiedAddons.value[addon.id] > 0) {
                occupiedAddons.value[addon.id] -= 1;
              }
            }
          }
          delete registrations.value[id];
          delete stashedData[id];
        }
        orderedTicketInstances.value = orderedTicketInstances.value.filter(
          (t) => !idsToRemove.includes(t._instanceId),
        );
      }

      localSelectionsMap[key].qty = qty;
      pendingQtyChange.value = null;
      updatePrimaryKeyIfNeeded();
    }
  }

  function handleDeleteGuest(ticket) {
    if (!ticket) return;
    const key = `${ticket.ticket_id}`;
    const currentQty = localSelectionsMap[key]?.qty || 0;
    if (currentQty === 0) return;

    const newQty = currentQty - 1;
    const instanceId = ticket._instanceId;
    const stash = stashedData[instanceId];
    const isFilled = stash && Object.keys(stash).length > 0;

    if (isFilled) {
      pendingQtyChange.value = { key, qty: newQty, idsToRemove: [instanceId] };
      openDeleteStashedFormConfirm.value = true;
    } else {
      delete registrations.value[instanceId];
      delete stashedData[instanceId];
      orderedTicketInstances.value = orderedTicketInstances.value.filter(
        (t) => t._instanceId !== instanceId,
      );
      localSelectionsMap[key].qty = newQty;
      pendingQtyChange.value = null;
      updatePrimaryKeyIfNeeded();
    }
  }

  const handleRegistrationSave = (ticketRegistration, instanceId) => {
    if (!instanceId) return;
    let addonErrors = [];

    const oldRegistration = registrations.value[instanceId];

    if (oldRegistration && oldRegistration.add_ons) {
      oldRegistration.add_ons.forEach((addon) => {
        if (occupiedAddons.value[addon.id] && occupiedAddons.value[addon.id] > 0) {
          occupiedAddons.value[addon.id] -= 1;
        }
      });
    }

    ticketRegistration.add_ons?.forEach((addon) => {
      const currentOccupied = occupiedAddons.value[addon.id] || 0;
      const newOccupied = currentOccupied + 1;
      const availableQty = addonSummary.value[addon.id] || 0;

      if (newOccupied > availableQty) {
        const errorMessage = `• ${addon.name} only has ${availableQty} available. Selection exceeds amount available.`;
        if (!addonErrors.includes(errorMessage)) {
          addonErrors.push(errorMessage);
        }
      }
    });

    if (addonErrors.length > 0) {
      if (oldRegistration && oldRegistration.add_ons) {
        oldRegistration.add_ons.forEach((addon) => {
          if (occupiedAddons.value[addon.id]) {
            occupiedAddons.value[addon.id] += 1;
          } else {
            occupiedAddons.value[addon.id] = 1;
          }
        });
      }

      const combinedErrorMessage = addonErrors.join('\n');
      snackbar.setSnackbar(combinedErrorMessage, 'error', 5000);
      return false; // Indicate save failure
    }

    ticketRegistration.add_ons?.forEach((addon) => {
      if (occupiedAddons.value[addon.id]) {
        occupiedAddons.value[addon.id] += 1;
      } else {
        occupiedAddons.value[addon.id] = 1;
      }
    });

    registrations.value[instanceId] = {
      ...ticketRegistration,
      _instanceId: instanceId,
    };
    return true;
  };

  const handleStashState = (e, instanceId) => {
    if (!instanceId) return;
    stashedData[instanceId] = e;
  };

  const handleContinueToCheckout = (opts = {}) => {
    const selectionsArray = Object.values(localSelectionsMap)
      .filter((item) => item.qty > 0)
      .map((item) => ({
        ticket: item.ticket,
        qty: item.qty,
      }));

    checkoutStore.setSelections(selectionsArray.length > 0 ? selectionsArray : null, null);

    const primaryRegData = primaryRegistrantTicket.value
      ? registrations.value[primaryRegistrantTicket.value._instanceId]
      : null;
    if (primaryRegData) {
      primaryRegData.is_primary = true;
    }
    const guestRegistrations = Object.values(registrations.value).filter(
      (r) =>
        primaryRegistrantTicket.value &&
        r &&
        r._instanceId !== primaryRegistrantTicket.value._instanceId,
    );

    const ticketId = primaryRegistrantTicket.value?.ticket_id;
    const ticketConfig = ticketId && ticketMap?.value ? ticketMap.value[ticketId] : null;
    let isGroupHost = false;
    if (ticketConfig?.allow_guest_invites) {
      if (ticketConfig.guest_invite_mode === 'automatic') {
        isGroupHost = true;
      } else if (ticketConfig.guest_invite_mode === 'opt_in') {
        isGroupHost = !!primaryRegData?.invite_others;
      }
    }

    let payload = {
      sponsor: null,
      invite_code: opts.invite_code || route.query.invite_code,
      is_group_host: isGroupHost,
      tables: null,
      tickets: checkoutStore?.selections?.map((t) => ({
        ticket_id: t.ticket.ticket_id,
        ticket_count: t.qty,
        registrations: [primaryRegData, ...guestRegistrations].filter(
          (r) => r && Number(r.ticket_id) === Number(t.ticket.ticket_id),
        ),
      })),
    };

    payload.bill_me_later = !!(opts && opts.bill_me_later);
    payload.total_amount = opts?.total_amount ?? null;
    payload.discount_code = opts?.discount_code || null;
    payload.discount_code_id = opts?.discount_code_id || null;

    postOrderDetails(payload).then(() => {
      clearLocalState();
    });
  };

  watch(loadingTicketData, (isLoading) => {
    if (!isLoading && !isEmpty(allTicketSelectionData.value)) {
      const storeSelections = checkoutStore.selections || [];
      const storeQtyMap = storeSelections.reduce((map, selection) => {
        const key = `${selection.ticket.ticket_id}`;
        map[key] = selection.qty;
        return map;
      }, {});

      const hasSelections = storeSelections.some((s) => s.qty > 0);

      allTicketSelectionData.value.forEach((ticket, index) => {
        const key = `${ticket.ticket_id}`;
        let qty = storeQtyMap[key] || 0;

        if (eventDataStore.eventData?.recruiter_mode && !hasSelections && index === 0) {
          qty = 1;
        }

        localSelectionsMap[key] = { ticket, qty: qty };
      });

      let initialInstances;
      const allSelections = Object.values(localSelectionsMap)
        .filter((item) => item.qty > 0)
        .map((item) => ({
          ticket: item.ticket,
          qty: item.qty,
        }));

      initialInstances = allSelections.flatMap((t) => {
        return Array.from(Array(t.qty), (_, i) => {
          const instanceId = `${t.ticket.ticket_id}_${i}`;
          return { ...t.ticket, _instanceId: instanceId };
        });
      });

      orderedTicketInstances.value = initialInstances;

      if (allSelections.length > 0) {
        primaryRegistrantSelectionKey.value = `${allSelections[0].ticket.ticket_id}`;
      }
    }
  });

  return {
    // State
    localSelectionsMap,
    registrations,
    stashedData,
    primaryRegistrantSelectionKey,
    openDeleteStashedFormConfirm,

    // Computed
    tickets,
    primaryRegistrantTicket,
    additionalRegistrantTickets,
    hasMultipleTicketTypes,
    primaryRegistrantDropdownItems,
    displayAddOnSummary,
    displayTicketSummary,
    completedRegistrationsCount,
    registrationProgress,
    disableContinue,

    // Methods
    handleQtyChange,
    handleRegistrationSave,
    handleStashState,
    handleDeleteGuest,
    handleDeleteStashedForm,
    handleContinueToCheckout,
  };
}
